import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Lenis from "lenis";
import { LOGO_URL, DEMO_KEY } from "@/lib/brand";
import {
  MessageSquare, Phone, ImagePlus, Zap, ShieldCheck, ArrowRight, Check,
  Smartphone, Code2, Sparkles, Menu, X, Mic, Send, Play,
} from "lucide-react";

const steps = [
  { n: "01", title: "Spin up your workspace", desc: "Get an isolated, multi-tenant workspace and invite your support agents in seconds." },
  { n: "02", title: "Drop one script tag", desc: "Paste the embed snippet. The chat launcher goes live on your site instantly." },
  { n: "03", title: "Reply from one console", desc: "Handle chats, voice calls and files from a single real-time command center." },
];

const NAV_LINKS = [
  { href: "#features", label: "Features" },
  { href: "#how", label: "Flow" },
  { href: "#pricing", label: "Pricing" },
];

function FeatureCard({ icon: Icon, title, desc, className = "", i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
      className={`group relative overflow-hidden rounded-2xl border border-white/[0.07] bg-[#091310] p-5 transition-colors hover:border-[#2ac67b]/40 sm:rounded-3xl sm:p-6 ${className}`}
      data-testid={`feature-card-${i}`}
    >
      <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[#2ac67b]/0 blur-2xl transition-all duration-500 group-hover:bg-[#2ac67b]/20" />
      <span className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-[#2ac67b]/20 bg-[#2ac67b]/10 text-[#2ac67b] transition-transform group-hover:scale-110 sm:h-12 sm:w-12 sm:rounded-2xl">
        <Icon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
      </span>
      <h3 className="relative mt-4 text-base font-semibold text-white sm:mt-5 sm:text-lg">{title}</h3>
      <p className="relative mt-1.5 text-sm text-white/50 sm:mt-2">{desc}</p>
    </motion.div>
  );
}

function Waveform() {
  const bars = [5, 9, 13, 8, 12, 6, 10, 14, 7, 11, 5, 9, 12, 6];
  return (
    <span className="flex items-center gap-[2px]" aria-hidden="true">
      {bars.map((h, i) => (
        <span key={i} className="w-[2.5px] rounded-full bg-black/40" style={{ height: h }} />
      ))}
    </span>
  );
}

function WidgetMock() {
  return (
    <div className="relative mx-auto w-full max-w-[320px] sm:max-w-[350px]">
      <div className="absolute -inset-8 rounded-[48px] bg-[#2ac67b]/10 blur-3xl" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.15 }}
        className="relative overflow-hidden rounded-[26px] border border-white/10 bg-[#0a1512] shadow-[0_24px_80px_rgba(0,0,0,0.6)]"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/5 bg-[#0e1f19] px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-[#2ac67b] text-sm font-bold text-black">
              A
              <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[#0e1f19] bg-[#2ac67b]" />
            </span>
            <div className="leading-tight">
              <p className="text-sm font-semibold text-white">Acme Support</p>
              <p className="text-[11px] text-[#2ac67b]">online · replies in minutes</p>
            </div>
          </div>
          <span className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-white/70">
            <Phone className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        </div>

        {/* Messages */}
        <div className="space-y-2.5 px-3.5 py-4 text-[13px]">
          <div className="max-w-[80%] rounded-2xl rounded-tl-md bg-white/[0.07] px-3 py-2 text-white/85">
            Hi there! 👋 How can we help you today?
          </div>
          <div className="ml-auto max-w-[80%] rounded-2xl rounded-tr-md bg-[#2ac67b] px-3 py-2 font-medium text-black">
            My order #AC-88213 hasn't shipped yet
          </div>
          <div className="ml-auto flex w-fit max-w-[80%] items-center gap-2 rounded-2xl rounded-tr-md bg-[#2ac67b] px-3 py-2 text-black">
            <Play className="h-3.5 w-3.5 fill-black" aria-hidden="true" />
            <Waveform />
            <span className="text-[11px] font-semibold">0:12</span>
          </div>
          <div className="max-w-[80%] rounded-2xl rounded-tl-md bg-white/[0.07] px-3 py-2 text-white/85">
            On it — checking with the warehouse 🚚
          </div>
          <div className="flex w-fit items-center gap-1 rounded-2xl rounded-tl-md bg-white/[0.07] px-3 py-2.5">
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                className="h-1.5 w-1.5 rounded-full bg-white/50"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.18 }}
              />
            ))}
          </div>
        </div>

        {/* Live call chip */}
        <div className="mx-3.5 mb-3 flex items-center justify-between rounded-xl border border-[#2ac67b]/25 bg-[#2ac67b]/10 px-3 py-2">
          <span className="flex items-center gap-2 text-[12px] font-medium text-[#2ac67b]">
            <Phone className="h-3.5 w-3.5" aria-hidden="true" /> Voice call · 02:41
          </span>
          <span className="h-2 w-2 animate-pulse rounded-full bg-[#2ac67b]" aria-hidden="true" />
        </div>

        {/* Input bar */}
        <div className="flex items-center gap-2 border-t border-white/5 bg-[#0e1f19] px-3.5 py-3">
          <span className="flex-1 rounded-full bg-white/[0.06] px-3.5 py-2 text-[13px] text-white/35">Type a message…</span>
          <span className="text-white/40"><Mic className="h-4 w-4" aria-hidden="true" /></span>
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2ac67b] text-black">
            <Send className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        </div>
      </motion.div>
    </div>
  );
}

function InboxMockCard() {
  const rows = [
    { name: "Kk Joshi", msg: "Sure, it's #AC-88213", active: true },
    { name: "Priya Sharma", msg: "Is the premium plan available…" },
    { name: "James Lee", msg: "🎤 Voice note · 0:12" },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="relative col-span-1 overflow-hidden rounded-2xl border border-white/[0.07] bg-[#091310] sm:rounded-3xl md:col-span-7"
    >
      <div className="pointer-events-none absolute inset-0 web3-grid opacity-60" aria-hidden="true" />
      <div className="relative flex flex-col gap-5 p-5 sm:p-7 md:flex-row md:items-center">
        <div className="min-w-0 flex-1">
          <Smartphone className="h-6 w-6 text-[#2ac67b] sm:h-7 sm:w-7" aria-hidden="true" />
          <h3 className="mt-3 font-display text-xl font-bold sm:text-2xl">WhatsApp-style inbox</h3>
          <p className="mt-2 max-w-md text-sm text-white/60">A familiar 3-pane workspace your agents already know how to use. Zero training, instant productivity.</p>
        </div>
        <div className="w-full shrink-0 space-y-2 md:w-[250px]">
          {rows.map((r) => (
            <div
              key={r.name}
              className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 ${r.active ? "border-[#2ac67b]/30 bg-[#2ac67b]/10" : "border-white/[0.06] bg-white/[0.03]"}`}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-white/80">
                {r.name[0]}
              </span>
              <div className="min-w-0 leading-tight">
                <p className="truncate text-[13px] font-semibold text-white">{r.name}</p>
                <p className="truncate text-[12px] text-white/45">{r.msg}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.1 });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#050a08] text-white">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-[#050a08]/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-2.5 sm:px-5 sm:py-3">
          <a href="#top" className="shrink-0">
            <img src={LOGO_URL} alt="SuperChat" className="h-9 w-auto object-contain sm:h-11" />
          </a>
          <nav className="hidden items-center gap-8 font-mono-x text-[12px] uppercase tracking-[0.15em] text-white/50 md:flex">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} className="transition-colors hover:text-[#2ac67b]">{l.label}</a>
            ))}
            <Link to="/docs" className="transition-colors hover:text-[#2ac67b]">Docs</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="hidden rounded-full border border-white/10 px-4 py-2 text-sm font-medium text-white/90 transition-colors hover:bg-white/5 sm:block" data-testid="nav-signin">Sign in</Link>
            <Link to="/login" className="rounded-full bg-[#2ac67b] px-4 py-2 text-sm font-semibold text-black transition-all hover:shadow-[0_0_20px_rgba(42,198,123,0.45)]" data-testid="nav-cta">Get started</Link>
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-white/80 md:hidden"
              onClick={() => setMenuOpen((o) => !o)}
              aria-expanded={menuOpen}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              data-testid="nav-menu-toggle"
            >
              {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <nav className="border-t border-white/5 bg-[#050a08]/95 px-4 py-3 backdrop-blur-xl md:hidden">
            <div className="flex flex-col gap-1 font-mono-x text-[12px] uppercase tracking-[0.15em]">
              {NAV_LINKS.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-2.5 text-white/70 transition-colors hover:bg-white/5 hover:text-[#2ac67b]">{l.label}</a>
              ))}
              <Link to="/docs" onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-2.5 text-white/70 transition-colors hover:bg-white/5 hover:text-[#2ac67b]">Docs</Link>
              <Link to="/login" onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-2.5 text-white/70 transition-colors hover:bg-white/5 hover:text-[#2ac67b]">Sign in</Link>
            </div>
          </nav>
        )}
      </header>

      {/* Hero */}
      <section id="top" className="relative overflow-hidden grain">
        <div className="pointer-events-none absolute inset-0 web3-grid opacity-70" aria-hidden="true" />
        <div className="pointer-events-none absolute left-1/2 top-16 h-[360px] w-[90vw] max-w-[700px] -translate-x-1/2 rounded-full bg-[#2ac67b]/15 blur-[130px]" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 pb-14 pt-10 sm:px-5 sm:pb-20 sm:pt-16 lg:grid-cols-2 lg:gap-14 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center lg:text-left"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 font-mono-x text-[10px] uppercase tracking-[0.2em] text-[#2ac67b] sm:text-[11px]">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> now in public beta
            </span>
            <h1 className="mt-5 font-display text-[34px] font-bold leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl">
              Customer support at <span className="neon-text">warp speed.</span>
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-base text-white/55 sm:mt-6 sm:text-lg lg:mx-0">
              SuperChat unifies live chat, in-browser voice calls and media sharing into one real-time console — with an embeddable WhatsApp-style widget your customers already know.
            </p>
            <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
              <Link to="/login" className="flex items-center justify-center gap-2 rounded-full bg-[#2ac67b] px-7 py-3.5 text-sm font-semibold text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_28px_rgba(42,198,123,0.5)]" data-testid="hero-cta">
                Start free <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <a href="#demo" className="flex items-center justify-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/5">
                Try live demo
              </a>
            </div>
            <div className="mx-auto mt-8 grid max-w-md grid-cols-3 gap-2 sm:mt-10 lg:mx-0">
              {[
                ["<1s", "message delivery"],
                ["0", "downloads for calls"],
                ["1 tag", "to install"],
              ].map(([big, small]) => (
                <div key={small} className="rounded-2xl border border-white/[0.07] bg-white/[0.03] px-2 py-3 sm:px-3">
                  <p className="font-display text-lg font-bold text-[#2ac67b] sm:text-xl">{big}</p>
                  <p className="mt-0.5 font-mono-x text-[9px] uppercase tracking-[0.12em] text-white/40 sm:text-[10px]">{small}</p>
                </div>
              ))}
            </div>
          </motion.div>
          <WidgetMock />
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-y border-white/5 bg-[#070f0c] py-4 sm:py-6">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-6 gap-y-2 px-4 font-mono-x text-[10px] uppercase tracking-[0.15em] text-white/40 sm:gap-x-10 sm:gap-y-3 sm:px-5 sm:text-[12px]">
          {["Realtime WebSocket", "WebRTC audio", "Media sharing", "Multi-tenant", "One-line embed"].map((t) => (
            <span key={t} className="flex items-center gap-1.5 sm:gap-2"><Check className="h-3.5 w-3.5 text-[#2ac67b] sm:h-4 sm:w-4" aria-hidden="true" /> {t}</span>
          ))}
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="mx-auto max-w-6xl scroll-mt-20 px-4 py-14 sm:px-5 sm:py-20 lg:py-24">
        <div className="max-w-2xl">
          <p className="font-mono-x text-[11px] uppercase tracking-[0.2em] text-[#2ac67b] sm:text-[12px]">// capabilities</p>
          <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:mt-3 sm:text-4xl">Everything you need to talk to customers.</h2>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-3 sm:mt-12 sm:gap-4 md:grid-cols-12">
          <FeatureCard i={0} className="md:col-span-5" icon={MessageSquare} title="Live text chat" desc="Realtime messaging with typing indicators, read receipts and instant sync across devices." />
          <FeatureCard i={1} className="md:col-span-4" icon={Phone} title="In-browser voice" desc="Escalate any chat to a crystal-clear WebRTC call — no downloads, no phone numbers." />
          <FeatureCard i={2} className="md:col-span-3" icon={ImagePlus} title="Media & voice notes" desc="Share images, files and record voice notes inline." />
          <InboxMockCard />
          <FeatureCard i={4} className="md:col-span-5" icon={Code2} title="One-line install" desc="Drop a single script tag on any site and your widget is live in under a minute. Full REST + WebSocket API included." />
          <FeatureCard i={6} className="md:col-span-6" icon={ShieldCheck} title="Multi-tenant & secure" desc="Isolated workspaces per business, JWT auth, and role-based access baked in from day one." />
          <FeatureCard i={7} className="md:col-span-6" icon={Zap} title="Real-time everything" desc="Messages, presence, typing and call signaling travel over persistent WebSocket rooms — sub-second delivery." />
        </div>
      </section>

      {/* How it works - timeline */}
      <section id="how" className="relative scroll-mt-20 bg-[#070f0c] py-14 sm:py-20 lg:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-5">
          <p className="font-mono-x text-[11px] uppercase tracking-[0.2em] text-[#2ac67b] sm:text-[12px]">// deploy flow</p>
          <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:mt-3 sm:text-4xl">Live in three steps.</h2>
          <div className="relative mt-8 pl-9 sm:mt-12 sm:pl-10">
            <div className="absolute left-[15px] top-2 bottom-2 w-px bg-gradient-to-b from-[#2ac67b] via-[#2ac67b]/40 to-transparent" aria-hidden="true" />
            {steps.map((s) => (
              <div key={s.n} className="relative mb-8 last:mb-0 sm:mb-10">
                <span className="absolute -left-[36px] flex h-8 w-8 items-center justify-center rounded-full border border-[#2ac67b]/40 bg-[#050a08] font-mono-x text-xs font-bold text-[#2ac67b] neon-glow sm:-left-[38px]">{s.n}</span>
                <h3 className="text-lg font-semibold text-white sm:text-xl">{s.title}</h3>
                <p className="mt-1 text-sm text-white/50 sm:mt-1.5">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo */}
      <section id="demo" className="mx-auto max-w-6xl scroll-mt-20 px-4 py-14 sm:px-5 sm:py-20 lg:py-24">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#091310] p-6 grain sm:rounded-[32px] sm:p-10 lg:p-14">
          <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#00e5ff]/10 blur-[120px]" aria-hidden="true" />
          <div className="pointer-events-none absolute -left-20 bottom-0 h-72 w-72 rounded-full bg-[#2ac67b]/15 blur-[120px]" aria-hidden="true" />
          <div className="relative grid items-center gap-8 sm:gap-10 lg:grid-cols-2">
            <div>
              <p className="font-mono-x text-[11px] uppercase tracking-[0.2em] text-[#2ac67b] sm:text-[12px]">// ungated demo</p>
              <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:mt-3 sm:text-4xl">Try SuperChat right now.</h2>
              <p className="mt-3 text-base text-white/55 sm:mt-4 sm:text-lg">
                A live demo workspace (<b className="text-white">Acme Support</b>) is ready to go. Open the full-screen widget to send a message, share a photo or start a voice call — then log in as an agent to reply.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:mt-7 sm:flex-row">
                <Link to="/login" className="rounded-full bg-[#2ac67b] px-6 py-3 text-center text-sm font-semibold text-black transition-all hover:shadow-[0_0_24px_rgba(42,198,123,0.45)]" data-testid="demo-agent-login">Open agent console</Link>
                <a href={`/widget/${DEMO_KEY}`} target="_blank" rel="noreferrer" className="rounded-full border border-white/15 px-6 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-white/5">Full-screen widget</a>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <a href={`/widget/${DEMO_KEY}`} target="_blank" rel="noreferrer" className="glass flex items-center gap-3 rounded-2xl px-5 py-4 transition-transform hover:scale-[1.02] sm:px-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#2ac67b] text-black neon-glow"><MessageSquare className="h-5 w-5" aria-hidden="true" /></span>
                <span className="text-sm font-medium text-white/80">Launch the live widget →</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="mx-auto max-w-3xl scroll-mt-20 px-4 pb-16 text-center sm:px-5 sm:pb-24">
        <span className="inline-flex items-center gap-2 rounded-full border border-[#2ac67b]/30 bg-[#2ac67b]/10 px-3.5 py-1.5 font-mono-x text-[10px] uppercase tracking-[0.2em] text-[#2ac67b] sm:text-[11px]">beta pricing</span>
        <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:mt-5 sm:text-4xl">Free while we're in beta.</h2>
        <p className="mt-3 text-base text-white/50 sm:mt-4 sm:text-lg">Unlimited agents, chats, calls and media during the beta. Lock in early-access pricing later.</p>
        <Link to="/login" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#2ac67b] px-8 py-3.5 text-sm font-semibold text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_28px_rgba(42,198,123,0.5)] sm:mt-8 sm:py-4" data-testid="pricing-cta">
          Claim your workspace <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-[#070f0c]">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-4 py-6 sm:flex-row sm:gap-4 sm:px-5 sm:py-8">
          <img src={LOGO_URL} alt="SuperChat" className="h-8 w-auto object-contain sm:h-10" />
          <p className="font-mono-x text-[10px] uppercase tracking-[0.15em] text-white/30 sm:text-[11px]">© {new Date().getFullYear()} superchat · built for support teams</p>
          <div className="flex gap-6 font-mono-x text-[11px] uppercase tracking-[0.15em] text-white/40 sm:text-[12px]">
            <Link to="/docs" className="hover:text-[#2ac67b]">Docs</Link>
            <Link to="/login" className="hover:text-[#2ac67b]">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
